import {
  and,
  type DocumentData,
  limit,
  or,
  orderBy,
  type QueryConstraint,
  type QueryFilterConstraint,
  where,
} from "firebase/firestore";
import { firestoreApi } from "../core/apis/firestoreApi";
import BaseApi from "../core/BaseApi";
import type BaseModuleModel from "../modules/core/BaseModuleModel";
import type {
  ModuleModelData,
  Snapshot,
} from "../modules/core/BaseModuleModel";
import type UserModel from "../users/UserModel";
import ActivityModel, { type ActivityModelData } from "./ActivityModel";
import { ActivityEventType } from "./types";

export default class ActivitiesApi extends BaseApi<
  ActivityModel,
  ActivityModelData
> {
  /* PRIVATE APIS */

  async getEventsForModulesByTimestamp(
    modules: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>[],
    user: UserModel,
  ) {
    const allEvents: {
      module: BaseModuleModel<Snapshot, ModuleModelData<Snapshot>>;
      event: ActivityModel;
    }[] = [];

    await Promise.all(
      modules.map(async (module) => {
        const lastActivity = user.data.moduleVisits[module.uid];

        const qs: QueryConstraint[] = [
          where("moduleUid", "==", module.uid),
          orderBy("timestamp", "desc"),
          limit(5),
        ];
        if (lastActivity) {
          qs.push(where("timestamp", ">=", lastActivity));
        }

        const querySnapshot = await firestoreApi.queryDocuments(this.db, ...qs);
        allEvents.push(
          ...querySnapshot.map((doc: DocumentData) => {
            return { module, event: this.fromFirestore(doc) };
          }),
        );
      }),
    );

    return allEvents.sort((a, b) =>
      a.event.data.timestamp.seconds < b.event.data.timestamp.seconds ? 1 : -1,
    );
  }

  async getEventsForModuleId(
    moduleUid: string,
    amount: number,
    includeAll = false,
  ) {
    const qs: QueryFilterConstraint[] = [where("moduleUid", "==", moduleUid)];

    if (!includeAll) {
      qs.push(
        or(
          where("type", "==", ActivityEventType.StateChange),
          where("type", "==", ActivityEventType.Comment),
          where("type", "==", ActivityEventType.Added),
        ),
      );
    }

    const querySnapshot = await firestoreApi.queryDocuments(
      this.db,
      and(...qs) as unknown as QueryConstraint,
      orderBy("timestamp"),
      limit(amount + 1),
    );

    this.store.load(
      querySnapshot.map((doc: DocumentData) => this.fromFirestore(doc)),
    );
  }

  /* NEEDED BASE API OVERRIDES */

  get db() {
    return `${this.dbPrefix}activities`;
  }

  get store() {
    return this.stores.activitiesStore;
  }

  fromFirestore(res: DocumentData): ActivityModel {
    return new ActivityModel(this.stores, this.api, {
      uid: res.id,
      ...res.data(),
    });
  }
}
